import { isErrorEnvelope } from '@ccp/api-client';
import { QueryClient } from '@tanstack/react-query';

/** How long a read stays fresh before a refetch is considered. */
const staleTimeMs = 30_000;

/**
 * The query client's defaults.
 *
 * Realtime events patch or invalidate caches by aggregate and sequence, so polling is off and
 * staleness is short rather than zero: the gateway is the freshness mechanism, not the interval.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: staleTimeMs,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        retry: (failureCount, error) => {
          // A refused or invalid request will be refused again; only retry what may recover.
          if (isErrorEnvelope(error) && !isRetryable(error.code)) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        // A command carries an idempotency key, so a retry is safe; the caller decides when.
        retry: false,
      },
    },
  });
}

const retryableCodes = new Set(['INFRASTRUCTURE_ERROR', 'INTEGRATION_ERROR']);

export function isRetryable(code: string): boolean {
  return retryableCodes.has(code);
}
